import React, { useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';

/**
 * UnifiedAuditDiff renders a key-level diff between two audit-log values.
 *
 * Each key in `union(old, new)` produces one row classified as:
 *   - `added`    — present in new only
 *   - `removed`  — present in old only
 *   - `changed`  — present in both, different value
 *   - `unchanged`— present in both, same value (hidden by default)
 *
 * Values with "password", "secret", or "token" in the key (case-insensitive)
 * are masked regardless of which side they appear on.
 */

type DiffKind = 'added' | 'removed' | 'changed' | 'unchanged';

interface DiffRow {
  key: string;
  kind: DiffKind;
  oldValue: unknown;
  newValue: unknown;
}

const SENSITIVE_KEY_RE = /pass|secret|token/i;

const formatValue = (value: unknown, key: string): string => {
  if (value === null || value === undefined) return '∅';
  if (SENSITIVE_KEY_RE.test(key)) {
    // Mask, but give a length hint so "changed" still reads differently.
    const s = typeof value === 'string' ? value : JSON.stringify(value);
    return s.length > 0 ? '••••' : '∅';
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const computeDiff = (oldVal: unknown, newVal: unknown): DiffRow[] => {
  const toDict = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const a = toDict(oldVal);
  const b = toDict(newVal);
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();

  return keys.map((key) => {
    const inOld = key in a;
    const inNew = key in b;
    const ov = a[key];
    const nv = b[key];
    let kind: DiffKind;
    if (inOld && inNew) {
      kind = JSON.stringify(ov) === JSON.stringify(nv) ? 'unchanged' : 'changed';
    } else if (inNew) {
      kind = 'added';
    } else {
      kind = 'removed';
    }
    return { key, kind, oldValue: ov, newValue: nv };
  });
};

interface UnifiedAuditDiffProps {
  oldValue: unknown;
  newValue: unknown;
  /** Show the "N unchanged keys" toggle. Default true. */
  showUnchangedToggle?: boolean;
  /** When true, non-dict values are rendered as raw JSON. */
  allowNonDictFallback?: boolean;
}

const UnifiedAuditDiff: React.FC<UnifiedAuditDiffProps> = ({
  oldValue,
  newValue,
  showUnchangedToggle = true,
  allowNonDictFallback = true,
}) => {
  const { t } = useI18n();
  const [showUnchanged, setShowUnchanged] = useState(false);

  const rows = useMemo(() => computeDiff(oldValue, newValue), [oldValue, newValue]);

  const nothing = oldValue == null && newValue == null;
  const bothNonDict =
    (oldValue != null || newValue != null) &&
    rows.length === 0 &&
    allowNonDictFallback;

  if (nothing) {
    return <div className="audit-diff-empty">{t('audit.row.noDiff')}</div>;
  }

  if (bothNonDict) {
    // Non-object payloads (arrays, scalars): fall back to raw side-by-side.
    return (
      <div className="audit-diff-fallback">
        <div>
          <div className="audit-diff-label">{t('audit.row.oldValue')}</div>
          <pre className="audit-diff-pre">
            {oldValue == null ? '—' : JSON.stringify(oldValue, null, 2)}
          </pre>
        </div>
        <div>
          <div className="audit-diff-label">{t('audit.row.newValue')}</div>
          <pre className="audit-diff-pre">
            {newValue == null ? '—' : JSON.stringify(newValue, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  const visible = showUnchanged ? rows : rows.filter((r) => r.kind !== 'unchanged');
  const unchangedCount = rows.filter((r) => r.kind === 'unchanged').length;
  const changedCount = rows.length - unchangedCount;

  return (
    <div className="audit-unified-diff" data-testid="audit-unified-diff">
      <div className="audit-unified-diff-summary">
        {t('audit.row.diffSummary', {
          changed: changedCount,
          total: rows.length,
        })}
      </div>
      <div className="audit-unified-diff-rows">
        {visible.map((row) => (
          <div
            key={row.key}
            className={`audit-diff-row audit-diff-${row.kind}`}
            data-testid={`audit-diff-row-${row.key}`}
          >
            <span className="audit-diff-marker" aria-hidden="true">
              {row.kind === 'added' && '+'}
              {row.kind === 'removed' && '−'}
              {row.kind === 'changed' && '~'}
              {row.kind === 'unchanged' && '='}
            </span>
            <span className="audit-diff-key">{row.key}</span>
            <span className="audit-diff-value">
              {row.kind === 'added' && (
                <span className="audit-diff-new">{formatValue(row.newValue, row.key)}</span>
              )}
              {row.kind === 'removed' && (
                <span className="audit-diff-old">{formatValue(row.oldValue, row.key)}</span>
              )}
              {row.kind === 'changed' && (
                <>
                  <span className="audit-diff-old">{formatValue(row.oldValue, row.key)}</span>
                  <span className="audit-diff-arrow" aria-hidden="true">
                    →
                  </span>
                  <span className="audit-diff-new">{formatValue(row.newValue, row.key)}</span>
                </>
              )}
              {row.kind === 'unchanged' && (
                <span className="audit-diff-same">{formatValue(row.newValue, row.key)}</span>
              )}
            </span>
          </div>
        ))}
      </div>
      {showUnchangedToggle && unchangedCount > 0 && (
        <button
          type="button"
          className="audit-unified-diff-toggle"
          onClick={() => setShowUnchanged((v) => !v)}
        >
          {showUnchanged
            ? t('audit.row.hideUnchanged', { count: unchangedCount })
            : t('audit.row.showUnchanged', { count: unchangedCount })}
        </button>
      )}
    </div>
  );
};

export default UnifiedAuditDiff;
